import React, { createContext, useContext, useEffect, useState } from 'react';
import { Package, Purchases } from '@revenuecat/purchases-js';
import type { PurchaseParams } from '@revenuecat/purchases-js';
import { supabase } from '../api/initSupabase';
import { localAnalytics } from '../utils/analytics';
import { logErrorsWithMessage } from '../utils/errors';
import { useAuth } from './AuthContext';

export interface PurchaseResult {
  success: boolean;
  error?: string;
  customerInfo?: any;
}

interface PurchasePackage {
  id: string;
  title: string;
  shortTitle: string;
  price: string;
  bonus: string;
  bonusAmount?: string;
  image: string;
}

interface PurchasesContextType {
  isInitialized: boolean;
  isPurchasesLoading: boolean;
  availablePackages: PurchasePackage[];
  formattedOfferings: PurchasePackage[];
  coinBalance: number;
  isUserPremium: boolean;
  nextFreeUnlock: Date | null;
  purchaseEmail: string | null;
  purchaseCoins: (productId: string) => Promise<PurchaseResult>;
  purchaseSubscription: (productId: string) => Promise<PurchaseResult>;
  purchaseCoinsGuest: (productId: string) => Promise<PurchaseResult>;
  purchaseSubscriptionGuest: (productId: string) => Promise<PurchaseResult>;
  refreshUserStatus: () => Promise<{
    coinBalance: number;
    isUserPremium: boolean;
    nextFreeUnlock: Date | null;
  }>;
  refreshPurchases: () => Promise<void>;
}

const PurchasesContext = createContext<PurchasesContextType | undefined>(
  undefined
);

export const usePurchases = () => {
  const context = useContext(PurchasesContext);
  if (!context) {
    throw new Error('usePurchases must be used within a PurchasesProvider');
  }
  return context;
};

export const PurchasesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isPurchasesLoading, setIsPurchasesLoading] = useState(false);
  const [availablePackages, setAvailablePackages] = useState<PurchasePackage[]>(
    []
  );
  const [formattedOfferings, setFormattedOfferings] = useState<
    PurchasePackage[]
  >([]);
  const [coinBalance, setCoinBalance] = useState(0);
  const [isUserPremium, setIsUserPremium] = useState(false);
  const [nextFreeUnlock, setNextFreeUnlock] = useState<Date | null>(null);
  const [purchaseEmail, setPurchaseEmail] = useState<string | null>(null);
  const { isAuthenticated, userId } = useAuth();

  const initialize = (userId: string): boolean => {
    try {
      const apiKey = process.env.REACT_APP_REVENUECAT_PUBLIC_KEY;
      if (!apiKey) {
        throw new Error('RevenueCat API key not configured');
      }

      Purchases.configure(apiKey, userId);
      setIsInitialized(true);
      console.log('Purchases SDK initialized for user:', userId);
      return true;
    } catch (error) {
      logErrorsWithMessage(error, 'Failed to initialize Purchases SDK');
      return false;
    }
  };

  const fetchPackage = async (
    packageIdentifier: string
  ): Promise<Package | null> => {
    if (!Purchases.isConfigured()) {
      return null;
    }

    try {
      const offerings = await Purchases.getSharedInstance().getOfferings();

      for (const offeringKey in offerings.all) {
        const offering = offerings.all[offeringKey];
        const pkg = offering.availablePackages.find(
          (p: Package) => p.webBillingProduct.identifier === packageIdentifier
        );
        if (pkg) return pkg;
      }

      return null;
    } catch (error) {
      logErrorsWithMessage(error, 'Failed to fetch package');
      return null;
    }
  };

  const getUserEmail = async (): Promise<string | null> => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      return session?.user?.email || null;
    } catch (error) {
      logErrorsWithMessage(error, 'Failed to get user email from session');
      return null;
    }
  };

  const fetchPurchaseEmail = async (
    userId: string,
    maxRetries: number = 3,
    delay: number = 500
  ): Promise<string | null> => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const { data, error } = await supabase
          .from('user_technical_details')
          .select('purchase_email')
          .eq('user_id', userId)
          .single();

        if (error && error.code !== 'PGRST116') {
          throw error;
        }

        if (data?.purchase_email) {
          return data.purchase_email;
        }

        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      } catch (error) {
        logErrorsWithMessage(
          error,
          `Failed to fetch purchase email (attempt ${attempt})`
        );
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    return null;
  };

  useEffect(() => {
    const initializePurchases = async () => {
      if (userId) {
        const initialized = initialize(userId);
        setIsInitialized(initialized);
        if (initialized) {
          await refreshPurchases();
        }
      } else {
        setIsInitialized(false);
      }
    };

    initializePurchases();
  }, [userId]);

  const retrieveAndSetPurchaseEmail = async (userId: string) => {
    const email = await fetchPurchaseEmail(userId);
    if (email) {
      setPurchaseEmail(email);
    }
  };

  useEffect(() => {
    const fetchEmail = async () => {
      if (userId && !purchaseEmail) {
        await retrieveAndSetPurchaseEmail(userId);
      }
    };
    fetchEmail();
  }, [userId]);

  const fetchUserStatusFromDB = async (targetUserId: string) => {
    const [userPremiumResult, isPremiumResult] = await Promise.all([
      supabase
        .from('user_premium')
        .select('coins, next_free_unlock, created_at')
        .eq('user_id', targetUserId)
        .single(),
      supabase.rpc('is_user_premium'),
    ]);

    const { data, error } = userPremiumResult;
    const { data: isPremiumData, error: premiumError } = isPremiumResult;

    if (error) {
      throw error;
    }

    if (premiumError) {
      throw premiumError;
    }

    return {
      coinBalance: data?.coins || 0,
      isUserPremium: !!isPremiumData,
      nextFreeUnlock: data?.next_free_unlock
        ? new Date(data.next_free_unlock)
        : null,
    };
  };

  const waitForUserStatusUpdate = async (
    targetUserId: string,
    condition: (status: {
      coinBalance: number;
      isUserPremium: boolean;
      nextFreeUnlock: Date | null;
    }) => boolean,
    maxRetries: number = 10,
    delay: number = 500
  ) => {
    let status: {
      coinBalance: number;
      isUserPremium: boolean;
      nextFreeUnlock: Date | null;
    } | null = null;
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        status = await fetchUserStatusFromDB(targetUserId);
        if (condition(status)) {
          setCoinBalance(status.coinBalance);
          setIsUserPremium(status.isUserPremium);
          setNextFreeUnlock(status.nextFreeUnlock);
          return status;
        }

        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      } catch (error) {
        logErrorsWithMessage(
          error,
          `Failed to check user status (attempt ${attempt})`
        );
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    return status;
  };

  const refreshUserStatus = async () => {
    try {
      if (!userId) {
        return {
          coinBalance: 0,
          isUserPremium: false,
          nextFreeUnlock: null,
        };
      }

      const userStatus = await fetchUserStatusFromDB(userId);

      setCoinBalance(userStatus.coinBalance);
      setIsUserPremium(userStatus.isUserPremium);
      setNextFreeUnlock(userStatus.nextFreeUnlock);

      return userStatus;
    } catch (error) {
      logErrorsWithMessage(error, 'Failed to get user status');
      const defaultStatus = {
        coinBalance: 0,
        isUserPremium: false,
        nextFreeUnlock: null,
      };
      setCoinBalance(0);
      setIsUserPremium(false);
      setNextFreeUnlock(null);
      return defaultStatus;
    }
  };

  const refreshPurchases = async () => {
    try {
      if (!userId) return;

      const [packagesResult] = await Promise.all([
        getAvailablePackagesInternal(),
      ]);

      if (packagesResult) {
        setAvailablePackages(packagesResult);
        setFormattedOfferings(packagesResult);
      }

      await refreshUserStatus();
    } catch (error) {
      console.error('Failed to refresh purchases:', error);
    }
  };

  const getAvailablePackagesInternal = async (): Promise<any[]> => {
    try {
      if (!Purchases.isConfigured()) {
        return [];
      }

      const offerings = await Purchases.getSharedInstance().getOfferings();
      const packages: any[] = [];

      for (const offeringKey in offerings.all) {
        const offering = offerings.all[offeringKey];
        offering.availablePackages.forEach((pkg: any) => {
          packages.push({
            id: pkg.webBillingProduct.identifier,
            title: pkg.webBillingProduct.displayName,
            shortTitle: pkg.webBillingProduct.displayName,
            price: pkg.webBillingProduct.priceString,
            bonus: '',
            image: '',
          });
        });
      }

      return packages;
    } catch (error) {
      logErrorsWithMessage(error, 'Failed to get available packages');
      return [];
    }
  };

  const purchaseCoins = async (productId: string): Promise<PurchaseResult> => {
    if (!userId) {
      return purchaseCoinsGuest(productId);
    }

    setIsPurchasesLoading(true);
    try {
      if (!Purchases.isConfigured()) {
        const initialized = initialize(userId);
        if (!initialized) {
          return {
            success: false,
            error: 'Failed to initialize payment system',
          };
        }
      }

      const initialCoinBalance = coinBalance;

      const [pkg, userEmail] = await Promise.all([
        fetchPackage(productId),
        getUserEmail(),
      ]);

      if (!pkg) {
        return {
          success: false,
          error: 'Product not found',
        };
      }

      const purchaseParams: PurchaseParams = { rcPackage: pkg };
      if (userEmail) {
        purchaseParams.customerEmail = userEmail;
      }

      const { customerInfo: newCustomerInfo } =
        await Purchases.getSharedInstance().purchase(purchaseParams);

      await waitForUserStatusUpdate(
        userId,
        status => status.coinBalance > initialCoinBalance
      );

      localAnalytics().logEvent('PurchaseSuccess', {
        productId,
        userId: userId,
        type: 'coins',
        emailPrePopulated: !!userEmail,
      });

      return {
        success: true,
        customerInfo: newCustomerInfo,
      };
    } catch (error: any) {
      if (error.errorCode === 1) {
        localAnalytics().logEvent('PurchaseCancelled', {
          productId,
          userId: userId,
          type: 'coins',
        });
        return {
          success: false,
          error: undefined,
        };
      }

      logErrorsWithMessage(error, 'Coin purchase failed');
      localAnalytics().logEvent('PurchaseError', {
        productId,
        userId: userId,
        type: 'coins',
        error: error.message || 'Unknown error',
      });

      return {
        success: false,
        error: error.message || 'Purchase failed',
      };
    } finally {
      setIsPurchasesLoading(false);
    }
  };

  const purchaseSubscription = async (
    productId: string
  ): Promise<PurchaseResult> => {
    if (!userId) {
      return purchaseSubscriptionGuest(productId);
    }

    setIsPurchasesLoading(true);
    try {
      if (!Purchases.isConfigured()) {
        const initialized = initialize(userId);
        if (!initialized) {
          return {
            success: false,
            error: 'Failed to initialize payment system',
          };
        }
      }

      const [pkg, userEmail] = await Promise.all([
        fetchPackage(productId),
        getUserEmail(),
      ]);

      if (!pkg) {
        return {
          success: false,
          error: 'Subscription product not found',
        };
      }

      const purchaseParams: PurchaseParams = { rcPackage: pkg };
      if (userEmail) {
        purchaseParams.customerEmail = userEmail;
      }

      const { customerInfo: newCustomerInfo } =
        await Purchases.getSharedInstance().purchase(purchaseParams);

      await waitForUserStatusUpdate(userId, status => status.isUserPremium);

      localAnalytics().logEvent('PurchaseSuccess', {
        productId,
        userId: userId,
        type: 'subscription',
        emailPrePopulated: !!userEmail,
      });

      await refreshUserStatus();
      return {
        success: true,
        customerInfo: newCustomerInfo,
      };
    } catch (error: any) {
      if (error.errorCode === 1) {
        localAnalytics().logEvent('PurchaseCancelled', {
          productId,
          userId: userId,
          type: 'subscription',
        });
        return {
          success: false,
          error: undefined,
        };
      }

      logErrorsWithMessage(error, 'Subscription purchase failed');
      localAnalytics().logEvent('PurchaseError', {
        productId,
        userId: userId,
        type: 'subscription',
        error: error.message || 'Unknown error',
      });

      return {
        success: false,
        error: error.message || 'Purchase failed',
      };
    } finally {
      setIsPurchasesLoading(false);
    }
  };

  const purchaseCoinsGuest = async (
    productId: string
  ): Promise<PurchaseResult> => {
    if (isAuthenticated) {
      return purchaseCoins(productId);
    }

    setIsPurchasesLoading(true);
    try {
      const {
        data: { user: anonymousUser },
        error: authError,
      } = await supabase.auth.signInAnonymously();

      if (authError || !anonymousUser) {
        throw new Error('Failed to create anonymous session');
      }
      const effectiveUserId = anonymousUser.id;
      const initialized = initialize(effectiveUserId);
      if (!initialized) {
        return {
          success: false,
          error: 'Failed to initialize payment system',
        };
      }

      const [initialUserStatus, pkg] = await Promise.all([
        fetchUserStatusFromDB(effectiveUserId),
        fetchPackage(productId),
      ]);

      if (!pkg) {
        return {
          success: false,
          error: 'Product not found',
        };
      }

      const purchaseParams: PurchaseParams = { rcPackage: pkg };

      const { customerInfo: newCustomerInfo } =
        await Purchases.getSharedInstance().purchase(purchaseParams);

      await waitForUserStatusUpdate(
        effectiveUserId,
        status => status.coinBalance > initialUserStatus.coinBalance
      );

      localAnalytics().logEvent('PurchaseSuccess', {
        productId,
        type: 'coins',
        isGuest: true,
        userId: effectiveUserId,
      });

      if (effectiveUserId) {
        void retrieveAndSetPurchaseEmail(effectiveUserId);
      }

      return {
        success: true,
        customerInfo: newCustomerInfo,
      };
    } catch (error: any) {
      if (error.errorCode === 1) {
        localAnalytics().logEvent('PurchaseCancelled', {
          productId,
          type: 'coins',
          isGuest: true,
        });
        return {
          success: false,
          error: undefined,
        };
      }

      logErrorsWithMessage(error, 'Guest coin purchase failed');
      localAnalytics().logEvent('PurchaseError', {
        productId,
        type: 'coins',
        isGuest: true,
        error: error.message || 'Unknown error',
      });

      return {
        success: false,
        error: error.message || 'Purchase failed',
      };
    } finally {
      setIsPurchasesLoading(false);
    }
  };

  const purchaseSubscriptionGuest = async (
    productId: string
  ): Promise<PurchaseResult> => {
    if (isAuthenticated) {
      return purchaseSubscription(productId);
    }

    setIsPurchasesLoading(true);
    try {
      const {
        data: { user: anonymousUser },
        error: authError,
      } = await supabase.auth.signInAnonymously();

      if (authError || !anonymousUser) {
        throw new Error('Failed to create anonymous session');
      }

      const effectiveUserId = anonymousUser.id;

      const initialized = initialize(effectiveUserId);
      if (!initialized) {
        return {
          success: false,
          error: 'Failed to initialize payment system',
        };
      }

      const pkg = await fetchPackage(productId);
      if (!pkg) {
        return {
          success: false,
          error: 'Subscription product not found',
        };
      }

      const purchaseParams: PurchaseParams = { rcPackage: pkg };

      const { customerInfo: newCustomerInfo } =
        await Purchases.getSharedInstance().purchase(purchaseParams);

      await waitForUserStatusUpdate(
        effectiveUserId,
        status => status.isUserPremium
      );

      localAnalytics().logEvent('PurchaseSuccess', {
        productId,
        type: 'subscription',
        isGuest: true,
        userId: effectiveUserId,
      });

      if (effectiveUserId) {
        void retrieveAndSetPurchaseEmail(effectiveUserId);
      }

      return {
        success: true,
        customerInfo: newCustomerInfo,
      };
    } catch (error: any) {
      if (error.errorCode === 1) {
        localAnalytics().logEvent('PurchaseCancelled', {
          productId,
          type: 'subscription',
          isGuest: true,
        });
        return {
          success: false,
          error: undefined,
        };
      }

      logErrorsWithMessage(error, 'Guest subscription purchase failed');
      localAnalytics().logEvent('PurchaseError', {
        productId,
        type: 'subscription',
        isGuest: true,
        error: error.message || 'Unknown error',
      });

      return {
        success: false,
        error: error.message || 'Purchase failed',
      };
    } finally {
      setIsPurchasesLoading(false);
    }
  };

  return (
    <PurchasesContext.Provider
      value={{
        isInitialized,
        isPurchasesLoading,
        availablePackages,
        formattedOfferings,
        coinBalance,
        isUserPremium,
        nextFreeUnlock,
        purchaseEmail,
        purchaseCoins,
        purchaseSubscription,
        purchaseCoinsGuest,
        purchaseSubscriptionGuest,
        refreshUserStatus,
        refreshPurchases,
      }}
    >
      {children}
    </PurchasesContext.Provider>
  );
};
